import React, { Fragment, useState, useEffect, useContext } from "react";
import Question from "./Question.js";
import Timer from "./Timer.js";
import Button from "../UI/Button.js";
import { ModalContext } from "../../Contexts/ModalContext.js";
import SessionContext from "../../Contexts/SessionContext.js";
import { Link, Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config.js";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  BookOpenIcon,
  LightBulbIcon,
  PencilIcon,
  AcademicCapIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { getSessionId, identityHeaders } from "../../utils/session";

// Fisher-Yates. Answer options are rendered by array index (the UI derives its
// A/B/C/D letters from the position) and correctness is validated by comparing
// the option *text*, so shuffling the array puts the right answer in a different
// slot each attempt without affecting grading.
const shuffle = (values) => {
  const out = [...values];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const PageQuiz = () => {
  const [shouldSubmitOnLastAnswer, setShouldSubmitOnLastAnswer] = useState(false);

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [results, setResults] = useState(null);
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [words, setWords] = useState([]);
  const modalContext = useContext(ModalContext);
  const { sessionId } = useContext(SessionContext);
  const history = useHistory();
  const location = useLocation();
  // The route is "/quiz/:course" (see App.js), so the URL parameter is `course`.
  // Destructuring `tense` here always produced undefined, which left courseTitle
  // null on any direct visit to a quiz URL (or a refresh) and made the Start Quiz
  // button silently do nothing. Accept either name and decode it.
  const params = useParams();
  const routeCourse = params.course || params.tense || null;
  // The messages and fallbacks below expect the raw (still encoded) value.
  const tense = routeCourse;

  // Get course data from navigation state (similar to original implementation)
  const courseId = location.state?.course_id || null;
  const courseTitle =
    location.state?.course_title ||
    (routeCourse ? decodeURIComponent(routeCourse) : null);

  const [secondsPerQuestion, setSecondsPerQuestion] = useState(60); //seconds per question (1 minute)
  const [startWith, setStartWith] = useState(5); //start with questions
  const [showSettingModal, setShowSettingModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Add double-click word capture (matching original implementation)
    const handleDblClick = () => {
      const word = window.getSelection().toString().trim();
      if (word.length > 0) {
        setWords((prev) => (prev.includes(word) ? prev : [...prev, word]));
      }
    };

    document.body.addEventListener("dblclick", handleDblClick);

    return () => {
      document.body.removeEventListener("dblclick", handleDblClick);
    };
  }, []);

  // Auto-save note when component unmounts or user navigates away
  // Save when quiz finishes
  useEffect(() => {
    if (quizFinished && note.trim()) {
      saveQuizNote();
    }
  }, [quizFinished]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (note.trim() && quizFinished) {
        saveQuizNote();
      }
    };
  }, []); // empty deps → runs only on unmount

  // Start quiz and fetch questions (matching original implementation)
  const startQuizWithQuestions = async (nbQuestions = startWith) => {
    if (!courseTitle) {
      console.error("❌ No course_title provided in route state.");
      console.log(
        "Available data - courseId:",
        courseId,
        "tense:",
        tense,
        "location.state:",
        location.state
      );
      setError(
        "Missing course title. Please go back and start the quiz from a course page."
      );
      return;
    }

    try {
      setLoading(true);
      console.log(
        `Starting quiz for course_title: ${courseTitle}, pathname: ${location.pathname}`
      );

      // Use course_title for consistent field mapping instead of course_id
      const response = await fetch(`${API_BASE_URL}${location.pathname}`, {
        method: "POST",
        body: JSON.stringify({
          nb_questions: nbQuestions,
          course_title: courseTitle, // Send course_title instead of course_ids
        }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          ...(localStorage.getItem("jstoken") && {
            Authorization: localStorage.getItem("jstoken"),
          }),
        },
      });

      const data = await response.json();
      console.log("Quiz questions response:", data);

      // Accept both formats: array or { questions: [...] }
      const questionsArray = Array.isArray(data) ? data : data.questions;

      if (questionsArray && questionsArray.length > 0) {
        // Transform questions from backend format to frontend format
        const transformedQuestions = questionsArray.map((question, idx) => {
          const correctAnswer =
            question.right_answer === "a"
              ? question.answer_title_a
              : question.right_answer === "b"
              ? question.answer_title_b
              : question.right_answer === "c"
              ? question.answer_title_c
              : question.right_answer === "d"
              ? question.answer_title_d
              : "";
          const options = shuffle(
            [
              question.answer_title_a,
              question.answer_title_b,
              question.answer_title_c,
              question.answer_title_d,
            ].filter((option) => option && option.trim() !== "") // Remove empty options
          );
          const slot = options.indexOf(correctAnswer);
          return {
            question_id: question.question_id ?? idx,
            question: question.question_title,
            question_type: "mcq",
            options,
            correct_answer: correctAnswer,
            // Re-derive the letter from the shuffled position so anything that
            // displays it matches what the student sees.
            right_answer_letter:
              slot >= 0 ? ["a", "b", "c", "d"][slot] : question.right_answer,
            // Keep original data for compatibility
            original: question,
          };
        });

        console.log("Transformed questions:", transformedQuestions);
        setQuizzes(transformedQuestions);
        setCurrentQuestionIndex(0);
        setQuizStarted(true);
        setQuizFinished(false);
        setResults(null);
        setSelectedAnswers({});
        setFadeOut(false);
        // Initialize timer: total time = number of questions * seconds per question
        setRemainingTime(transformedQuestions.length * secondsPerQuestion);
      } else {
        setError("No questions found for this course.");
      }
    } catch (err) {
      console.error("Failed to fetch questions:", err);
      setError("Could not start the quiz. Failed to load questions.");
    } finally {
      setLoading(false);
    }
  };

  // Save quiz note function (matching original logic)
  const saveQuizNote = async () => {
    if (!note.trim()) return;

    try {
      // Generate session ID for anonymous users if not exists (use same key as MistakesTracker)
      let sessionId = localStorage.getItem("session");
      if (!sessionId && !localStorage.getItem("jstoken")) {
        sessionId =
          getSessionId();
        localStorage.setItem("session", sessionId);
      }

      const noteData = {
        note: note,
        session_name:
          localStorage.getItem("session") ||
          new Date().toISOString().split("T")[0] + "_1",
        course_id: courseId || courseTitle, // Use courseTitle if courseId not available
        quiz_score: `${results?.correctAnswers || 0}/${
          results?.totalQuestions || 0
        }`,
        timestamp: new Date().toISOString(),
        ...(sessionId && { sessionId: sessionId }),
      };

      // Use existing notepad API endpoint
      const response = await fetch(`${API_BASE_URL}/notepad`, {
        method: "POST",
        body: JSON.stringify(noteData),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          ...(localStorage.getItem("jstoken") && {
            Authorization: `Bearer ${localStorage.getItem("jstoken")}`,
          }),
          ...(sessionId && { "x-session-id": sessionId }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Notepad save error:", errorData);
        throw new Error(errorData.error || "Failed to save note");
      }

      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 3000);
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  // Handle answer selection
  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  // Navigate to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < quizzes.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Navigate to previous question
  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Handle quiz submission
  const submitQuiz = async () => {
    // Calculate results
    const normalize = (str) => (str ?? "").toString().trim().toLowerCase();
    const correctAnswers = quizzes.filter(
      (question) =>
        normalize(selectedAnswers[question.question_id]) === normalize(question.correct_answer)
    ).length;

    const incorrectQuestions = quizzes.filter(
      (question) =>
        normalize(selectedAnswers[question.question_id]) !== normalize(question.correct_answer)
    );
    const missed_questions = quizzes.filter(
      (question) => selectedAnswers[question.question_id] === undefined
    );
    const missed_questions_ids = missed_questions.map((q) => q.question_id);

    const resultData = {
      totalQuestions: quizzes.length,
      correctAnswers,
      incorrectAnswers: quizzes.length - correctAnswers,
      score: (correctAnswers / quizzes.length) * 100,
      timeTaken: secondsPerQuestion * quizzes.length - remainingTime,
      incorrectQuestions: incorrectQuestions.map((q) => ({
        question_id: q.question_id,
        question: q.question,
        correct_answer: q.correct_answer,
        user_answer: selectedAnswers[q.question_id] || "No answer",
        tense_id: q.tense_id,
      })),
    };

    // Save incorrect answers as mistake questions to the database
    await saveMistakesToDatabase(incorrectQuestions);

    // --- NEW: Update dashboard on quiz finish ---
    try {
      let sessionId = localStorage.getItem("session");
      if (!sessionId && !localStorage.getItem("jstoken")) {
        sessionId =
          getSessionId();
        localStorage.setItem("session", sessionId);
      }
      // Find the course object by title to get its string course_id
      let stringCourseId = courseId;
      if (courseTitle && (!courseId || typeof courseId === "number")) {
        // Try to get courses from localStorage or a global variable if available
        let courses = [];
        try {
          courses = JSON.parse(localStorage.getItem("courses")) || [];
        } catch {}
        const foundCourse = courses.find(
          (c) =>
            c.course_title &&
            c.course_title.toLowerCase() === courseTitle.toLowerCase()
        );
        if (foundCourse && foundCourse.course_id) {
          stringCourseId = foundCourse.course_id;
        } else {
          stringCourseId = courseTitle;
        }
      }
      // Prepare user answers array for dashboard
      const userAnswersArray = quizzes.map((question, index) => ({
        question_id: question.question_id,
        user_answer: selectedAnswers[question.question_id] ?? "",
        correct_answer: question.correct_answer,
        is_correct:
          normalize(selectedAnswers[question.question_id]) === normalize(question.correct_answer),
        question_index: index,
        // Include full question text and metadata for dashboard modal
        question_text: question.question || question.text || question.question_title || question.question_title || '',
        options: question.options || [],
        right_answer_letter: question.right_answer_letter || '',
        explanation: question.original?.explanation || '',
        // Add any other fields needed for review display
      }));

      // session_name is NOT NULL in the API schema, and SessionContext can still be
      // empty on a direct visit, so fall back to a stored id or a fresh one instead
      // of posting a payload the API rejects.
      const sessionKey =
        sessionId ||
        localStorage.getItem("session") ||
        localStorage.getItem("sessionId") ||
        getSessionId();
      // resultat_batch.course_id is an integer column, so attaching the course
      // *title* here made Postgres reject the whole insert (22P02) and no quiz
      // result was ever stored. Resolve a numeric id: the one carried by the
      // fetched questions, then navigation state, then 0.
      const numericCourseId = (() => {
        const sources = [
          quizzes[0]?.original?.course_ids,
          quizzes[0]?.original?.course_id,
          courseId,
        ];
        for (const src of sources) {
          const m = String(src ?? "").match(/\d+/);
          if (m) return Number(m[0]);
        }
        return 0;
      })();
      const dashboardPayload = {
        course_id: numericCourseId,
        nb_good: correctAnswers,
        total_question: quizzes.length,
        time_per_question: secondsPerQuestion,
        time_remaining: typeof remainingTime === "number" ? remainingTime : 0,
        session_name: sessionKey,
        timestamp: new Date().toISOString(),
        user_answers: userAnswersArray, // Add user answers to dashboard
        ...(sessionId && { sessionId: sessionId }),
      };

      const headers = {
        "Content-Type": "application/json",
        ...(localStorage.getItem("jstoken") && {
          Authorization: `Bearer ${localStorage.getItem("jstoken")}`,
        }),
        ...(sessionId && { "x-session-id": sessionId }),
      };
      console.log("[Frontend] Sending dashboard payload:", dashboardPayload);
      const resp = await fetch(`${API_BASE_URL}/dashboard`, {
        method: "POST",
        body: JSON.stringify(dashboardPayload),
        headers,
      });
      if (!resp.ok) {
        const errorData = await resp.json();
        console.error("Dashboard update error:", errorData);
      } else {
        const responseData = await resp.json();
        console.log("Dashboard updated after quiz finish:", responseData);
      }
    } catch (err) {
      console.error("Failed to update dashboard after quiz:", err);
    }
    // --- END NEW ---

    setResults(resultData);
    setQuizFinished(true);

    console.log("Quiz completed:", resultData);
  };

  // Save mistake questions to database
  const saveMistakesToDatabase = async (incorrectQuestions) => {
    try {
      // Generate session ID for anonymous users if not exists (use same key as MistakesTracker)
      let sessionId = localStorage.getItem("session");
      if (!sessionId && !localStorage.getItem("jstoken")) {
        sessionId =
          getSessionId();
        localStorage.setItem("session", sessionId);
      }

      // Save each incorrect question as a mistake
      for (const question of incorrectQuestions) {
        const mistakeData = {
          // Backend fields
          questions_wrong_id: question.question_id,
          session_name:
            localStorage.getItem("session") ||
            new Date().toISOString().split("T")[0] + "_quiz",
          selected_option: selectedAnswers[question.question_id] || "",
          time_taken: Math.floor(
            (secondsPerQuestion * quizzes.length - remainingTime) /
              quizzes.length
          ),
          mark_for_review: false,

          // Frontend compatibility fields
          question_id: question.question_id,
          user_answer: selectedAnswers[question.question_id] || "",
          correct_option: question.correct_answer,
          correct_answer: question.correct_answer,
          explanation:
            question.original?.explanation ||
            question.original?.course_data ||
            "No explanation available",
          // user_mistake.course_id is an integer column too: sending the course
          // title made the detailed insert fail, which then fell back to a minimal
          // row without the options or explanation.
          course_id: (() => {
            const src =
              question.original?.course_ids ??
              question.original?.course_id ??
              courseId;
            const m = String(src ?? "").match(/\d+/);
            return m ? Number(m[0]) : 0;
          })(),
          course_name: courseTitle || tense || "Unknown Course",
          created_at: new Date().toISOString(),

          // Session handling
          ...(sessionId && { sessionId: sessionId }),
        };

        const response = await fetch(`${API_BASE_URL}/mistakequestion`, {
          method: "POST",
          body: JSON.stringify(mistakeData),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            ...(localStorage.getItem("jstoken") && {
              Authorization: `Bearer ${localStorage.getItem("jstoken")}`,
            }),
            ...(sessionId && { "x-session-id": sessionId }),
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Failed to save mistake question:", errorData);
        } else {
          console.log(`Saved mistake for question ${question.question_id}`);
        }
      }
    } catch (error) {
      console.error("Error saving mistakes to database:", error);
    }
  };

  // Reset quiz state
  const resetQuiz = () => {
    setQuizzes([]);
    setQuizStarted(false);
    setQuizFinished(false);
    setResults(null);
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
  };

  // Timer expired callback
  const handleTimeExpired = () => {
    alert(
      "Time's Up! Your time for this quiz has expired. Let's see your results!"
    );
    submitQuiz();
  };

  // Determine difficulty badge color
  const getDifficultyColor = (level) => {
    switch (level) {
      case 1:
        return "bg-green-100 text-green-800";
      case 2:
        return "bg-yellow-100 text-yellow-800";
      case 3:
        return "bg-orange-100 text-orange-800";
      case 4:
      case 5:
        return "bg-red-100 text-red-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  // Quiz Questions View
  const [fadeOut, setFadeOut] = useState(false);

  const renderQuizQuestions = () => {
    // Add safety checks for transformed question data
    if (!quizzes || quizzes.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-6 text-center">
          <ExclamationCircleIcon className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Quiz Questions Available
          </h3>
          <p className="text-gray-600 mb-4">
            Please select a quiz from the available options.
          </p>
          <Button onClick={() => setQuizStarted(false)}>
            Back to Quiz Selection
          </Button>
        </div>
      );
    }

    // const currentQuestion = quizzes[currentQuestionIndex];
    // return (
    //   <div className="bg-white rounded-lg shadow-lg p-6">
    //     <div className="mb-6 flex justify-between items-center">
    //       <h2 className="text-xl font-bold text-gray-800">Quiz</h2>
    //       <Timer
    //         initialTime={remainingTime}
    //         onTimeUpdate={setRemainingTime}

  const currentQuestion = quizzes[currentQuestionIndex];
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Quiz</h2>
        <Timer
          initialTime={remainingTime}
          onTimeUpdate={setRemainingTime}
          onTimeExpired={handleTimeExpired}
        />
      </div>

        <div className="h-1 w-full bg-gray-200 rounded-full mb-6">
          <div
            className="h-1 bg-blue-500 rounded-full"
            style={{
              width: `${((currentQuestionIndex + 1) / quizzes.length) * 100}%`,
            }}
          ></div>
        </div>

        <div
          className={`transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
        >
          <Question
            questionData={currentQuestion}
            selectedAnswer={selectedAnswers[currentQuestion.question_id]}
            onAnswerSelect={(answer) => {
              handleAnswerSelect(currentQuestion.question_id, answer);
              setFadeOut(true);
              setTimeout(() => {
                if (currentQuestionIndex === quizzes.length - 1) {
                  setShouldSubmitOnLastAnswer(true);
                } else {
                  setCurrentQuestionIndex((idx) => idx + 1);
                  setFadeOut(false);
                }
              }, 350); // fade out, then switch
            }}
          />
        </div>
      </div>
    );
  };

  // Quiz Results View

  const renderQuizResults = () => {
    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 sm:p-6 text-white">
          <h2 className="text-xl font-bold">Quiz Results</h2>
          <h3 className="mt-1 text-lg">{courseTitle || "Quiz"}</h3>
        </div>

        <div className="p-4 sm:p-6">
          {/* Three tiles stacked vertically ate most of a phone screen */}
          <div className="mb-6 grid grid-cols-3 gap-2 sm:gap-3">
            <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl sm:text-3xl font-bold text-gray-800">
                {results.score.toFixed(0)}%
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Score</div>
            </div>
            <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl sm:text-3xl font-bold text-green-600">
                {results.correctAnswers}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Correct</div>
            </div>
            <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl sm:text-3xl font-bold text-red-600">
                {results.incorrectAnswers}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Incorrect</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-gray-700 font-medium mb-2">Time taken:</div>
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
              <span>
                {Math.floor(results.timeTaken / 60)} minutes{" "}
                {results.timeTaken % 60} seconds
              </span>
            </div>
          </div>

          <h3 className="font-bold text-lg text-gray-800 mb-4">
            Question Review
          </h3>
          <div className="space-y-4 sm:max-h-96 sm:overflow-y-auto">
            {quizzes.map((question) => {
              const userAnswer =
                selectedAnswers[question.question_id] || "No answer";
              const isCorrect = userAnswer === question.correct_answer;

              return (
                <div
                  key={question.question_id}
                  className={`p-4 border rounded-lg ${
                    isCorrect
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  <div className="flex items-start">
                    {isCorrect ? (
                      <CheckCircleIcon className="h-5 w-5 mt-0.5 mr-2 text-green-600" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 mt-0.5 mr-2 text-red-600" />
                    )}
                    <div>
                      <p className="font-medium text-gray-800">
                        {question.question}
                      </p>
                      <div className="mt-2">
                        <p>
                          <span className="text-sm font-medium text-gray-500">
                            Your answer:{" "}
                          </span>
                          <span
                            className={
                              isCorrect ? "text-green-600" : "text-red-600"
                            }
                          >
                            {userAnswer}
                          </span>
                        </p>
                        {!isCorrect && (
                          <p className="mt-1">
                            <span className="text-sm font-medium text-gray-500">
                              Correct answer:{" "}
                            </span>
                            <span className="text-green-600">
                              {question.correct_answer}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Words looked up during quiz (matching original implementation) */}
          {words.length > 0 && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center">
                📚 Words You Searched During the Quiz
              </h4>
              <div className="flex flex-wrap gap-2">
                {words.map((word, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Note-taking section (matching original implementation) */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2 flex items-center">
              📝 Take a Note for This Quiz
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              You can take a note for this quiz. It'll be saved in your
              notepad.
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Write your thoughts about this quiz session..."
              className="w-full h-24 p-3 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {noteSaved && (
              <div className="mt-2 text-sm text-green-600 flex items-center">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Note saved successfully!
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-3">
            <Button
              onClick={() => {
                saveQuizNote();
                resetQuiz();
              }}
            >
              Try Another Quiz
            </Button>
            <Button
              onClick={() => {
                saveQuizNote();
                history.push("/notepad");
              }}
              className="bg-gray-500 hover:bg-gray-600"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Submit quiz after last answer state is updated
  useEffect(() => {
    if (shouldSubmitOnLastAnswer) {
      submitQuiz();
      setShouldSubmitOnLastAnswer(false);
    }
  }, [shouldSubmitOnLastAnswer]);

  // Main render
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          Quiz Practice
        </h1>
        <p className="mt-2 text-gray-600">
          Improve your language skills with interactive quizzes
        </p>
      </div>

      {/* Loading State */}
      {/* {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading quizzes...</p>
          </div>
        </div>
      )} */}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">
            Error Loading Quizzes
          </h3>
          <p className="text-red-700 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      )}

      {/* No Quizzes Available */}
      {!loading && !error && quizzes.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
          <h3 className="text-lg font-medium text-yellow-900 mb-2">
            No Quizzes Available
          </h3>
          <p className="text-yellow-700 mb-4">
            {tense
              ? `No quizzes found for "${decodeURIComponent(tense)}".`
              : "No quizzes are currently available."}
          </p>
          <Link to="/">
            <Button>Back to Tense Map</Button>
          </Link>
        </div>
      )}

      {/* Quiz Results */}
      {quizFinished && renderQuizResults()}

      {/* Quiz Settings/Start Screen (show only before quiz starts) */}
      {!quizStarted && !quizFinished && (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Quiz Settings
          </h3>
          <p className="text-gray-600 mb-6">
            Configure your quiz preferences and start when ready.
          </p>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time per question (seconds)
              </label>
              <select
                value={secondsPerQuestion}
                onChange={(e) =>
                  setSecondsPerQuestion(parseInt(e.target.value))
                }
                className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2.5 text-base"
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of questions
              </label>
              <select
                value={startWith}
                onChange={(e) => setStartWith(parseInt(e.target.value))}
                className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2.5 text-base"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={40}>40</option>
              </select>
            </div>
          </div>

          <Button onClick={() => startQuizWithQuestions(startWith)}>
            Start Quiz
          </Button>
        </div>
      )}
      {!loading &&
        !error &&
        quizStarted &&
        !quizFinished &&
        renderQuizQuestions()}
    </div>
  );
};

export default PageQuiz;
